import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, 
  FileText, 
  CheckCircle, 
  Clock, 
  User,
  Phone,
  Mail,
  Calendar,
  MapPin
} from 'lucide-react';
import Header from '../common/Header';

const StudentDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-teal-100">
      <Header title="Student Dashboard" showBack={false} />
      
      <div className="container mx-auto px-4 py-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center space-x-4">
            <div className="bg-teal-100 rounded-full p-3">
              <GraduationCap className="h-8 w-8 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Welcome back, {user?.firstName || 'Student'}!
              </h1>
              <p className="text-gray-600">
                Manage your student life and outing requests
              </p>
            </div>
          </div>
          
          {/* User Info */}
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center space-x-3 text-sm text-gray-600">
              <User className="h-4 w-4" />
              <span>{user?.firstName} {user?.lastName}</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-600">
              <Phone className="h-4 w-4" />
              <span>{user?.mobile}</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-600">
              <Mail className="h-4 w-4" />
              <span>{user?.email}</span>
            </div>
            <div className="flex items-center space-x-3 text-sm text-gray-600">
              <Calendar className="h-4 w-4" />
              <span>Student</span>
            </div>
          </div>

          {/* Zoho CRM Info */}
          {user?.zohoData && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-800 mb-2">Zoho CRM Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                {user.zohoData.leadId && (
                  <div>
                    <span className="font-medium text-blue-700">Lead ID:</span>
                    <span className="ml-2 text-blue-600">{user.zohoData.leadId}</span>
                  </div>
                )}
                {user.zohoData.applicationId && (
                  <div>
                    <span className="font-medium text-blue-700">Application ID:</span>
                    <span className="ml-2 text-blue-600">{user.zohoData.applicationId}</span>
                  </div>
                )}
                {user.zohoData.leadStage && (
                  <div>
                    <span className="font-medium text-blue-700">Lead Stage:</span>
                    <span className="ml-2 text-blue-600">{user.zohoData.leadStage}</span>
                  </div>
                )}
                {/* NEW: Application Stage 1 Status */}
                {user.zohoData.Application_Stage_1_Filled && (
                  <div>
                    <span className="font-medium text-blue-700">Stage 1 Status:</span>
                    <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${
                      user.zohoData.Application_Stage_1_Filled === 'Yes' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {user.zohoData.Application_Stage_1_Filled === 'Yes' ? 'Completed' : 'Pending'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Student Life Section */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-blue-100 rounded-lg p-2">
              <GraduationCap className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Student Life</h2>
            <span className="bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
              Active
            </span>
          </div>
          
          {/* Outing Management Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Outing Management</h2>
                <p className="text-gray-600">Request and track your outing permissions</p>
              </div>
              <button
                onClick={() => navigate('/outing/student')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
              >
                <Calendar className="h-5 w-5 mr-2" />
                Manage Outings
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center space-x-3 mb-3">
                  <Calendar className="h-6 w-6 text-blue-600" />
                  <h3 className="font-semibold text-blue-900">Create Request</h3>
                </div>
                <p className="text-sm text-blue-700 mb-3">
                  Submit new outing requests with details about dates, times, and reasons
                </p>
                <button
                  onClick={() => navigate('/outing/student')}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Create Request →
                </button>
              </div>
              
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center space-x-3 mb-3">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  <h3 className="font-semibold text-green-900">Track Status</h3>
                </div>
                <p className="text-sm text-green-700 mb-3">
                  Monitor the approval status of your outing requests in real-time
                </p>
                <button
                  onClick={() => navigate('/outing/student')}
                  className="text-green-600 hover:text-green-700 text-sm font-medium"
                >
                  View Status →
                </button>
              </div>
              
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center space-x-3 mb-3">
                  <MapPin className="h-6 w-6 text-purple-600" />
                  <h3 className="font-semibold text-purple-900">History</h3>
                </div>
                <p className="text-sm text-purple-700 mb-3">
                  View your complete outing history and past permissions
                </p>
                <button
                  onClick={() => navigate('/outing/student')}
                  className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                >
                  View History →
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Additional Student Features */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="bg-purple-100 rounded-lg p-2">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Quick Actions</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Attendance Tracking - Placeholder */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 opacity-60">
              <div className="flex items-center space-x-3 mb-3">
                <CheckCircle className="h-6 w-6 text-gray-400" />
                <h3 className="font-semibold text-gray-500">Attendance</h3>
              </div>
              <p className="text-sm text-gray-400 mb-3">
                View your daily attendance and punctuality records
              </p>
              <div className="text-gray-400 text-sm font-medium">Coming Soon</div>
            </div>
            
            {/* Academic Progress - Placeholder */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 opacity-60">
              <div className="flex items-center space-x-3 mb-3">
                <FileText className="h-6 w-6 text-gray-400" />
                <h3 className="font-semibold text-gray-500">Academic Progress</h3>
              </div>
              <p className="text-sm text-gray-400 mb-3">
                Check your grades, assignments, and performance
              </p>
              <div className="text-gray-400 text-sm font-medium">Coming Soon</div>
            </div>
            
            {/* Library Access - Placeholder */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 opacity-60">
              <div className="flex items-center space-x-3 mb-3">
                <GraduationCap className="h-6 w-6 text-gray-400" />
                <h3 className="font-semibold text-gray-500">Library</h3>
              </div>
              <p className="text-sm text-gray-400 mb-3">
                Access digital library resources and book catalog
              </p>
              <div className="text-gray-400 text-sm font-medium">Coming Soon</div>
            </div>
          </div>
        </div>

        {/* Student Notices Section */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Student Notices & Updates</h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-4 bg-blue-50 rounded-lg">
              <Calendar className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-blue-900">Outing Request Deadline</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Submit outing requests at least 24 hours in advance for approval.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 p-4 bg-green-50 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-900">Campus Rules</h3>
                <p className="text-sm text-green-700 mt-1">
                  Remember to follow all campus rules and regulations during outings.
                </p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3 p-4 bg-yellow-50 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-900">Return Time</h3>
                <p className="text-sm text-yellow-700 mt-1">
                  Ensure you return to campus by the approved return time for all outings.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Logout Button */}
        <div className="mt-8 text-center">
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;


